import React, { useEffect, useState, useCallback } from "react";
import { Kanban as KanbanIcon, Clock, Truck, Package as PackageIcon, ClipboardList, Store, Plus, Sparkles } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, relDay } from "@/lib/format";
import { PRIORITY_BADGE, PRIORITY_LABEL } from "@/lib/constants";
import { Loading, PageHeader, Badge, Button, Card, EmptyState } from "@/components/common";
import OrderDetailModal from "@/components/OrderDetailModal";
import MaterialUsageModal from "@/components/MaterialUsageModal";

const COLS = [
  ["queue", "Menunggu Dirangkai", "border-t-sky-400"],
  ["arranging", "Sedang Dirangkai", "border-t-amber-400"],
  ["qc", "Siap QC", "border-t-emerald-400"],
  ["ready_pickup", "Siap Diambil", "border-t-green-400"],
  ["ready_delivery", "Siap Dikirim", "border-t-sky-500"],
  ["completed", "Selesai", "border-t-slate-300"],
];

export default function Kanban() {
  const { branch } = useAuth();
  const [tab, setTab] = useState("orders");
  const [cols, setCols] = useState({});
  const [transitions, setTransitions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [usageOrder, setUsageOrder] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = branch ? { branch_id: branch.id } : {};
      const [k, center] = await Promise.all([
        api.get("/orders/kanban", params),
        api.get("/orders/center", { ...params, limit: 1 }).catch(() => ({})),
      ]);
      setCols(k);
      setTransitions(center.transitions || {});
    } finally {
      setLoading(false);
    }
  }, [branch]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <PageHeader title="Tugas Perangkai" subtitle={branch?.name} icon={KanbanIcon} />

      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab("orders")} data-testid="tab-orders"
          className={`inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-medium ${tab === "orders" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
          <ClipboardList size={16} /> Orderan Customer
        </button>
        <button onClick={() => setTab("display")} data-testid="tab-display"
          className={`inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-medium ${tab === "display" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
          <Store size={16} /> Produk Display Toko
        </button>
      </div>

      {tab === "orders" ? (
        loading ? <Loading /> : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {COLS.map(([key, label, border]) => {
              const items = cols[key] || [];
              const canUsage = key === "queue" || key === "arranging";
              return (
                <div key={key} className="w-72 shrink-0">
                  <div className={`bg-white rounded-t-xl border-t-4 ${border} border-x border-slate-200 px-4 py-3 flex items-center justify-between`}>
                    <span className="font-semibold text-slate-700 text-sm">{label}</span>
                    <span className="text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">{items.length}</span>
                  </div>
                  <div className="bg-slate-50/70 border-x border-b border-slate-200 rounded-b-xl p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-18rem)] overflow-y-auto">
                    {items.map((o) => (
                      <div key={o.id} className="bg-white rounded-xl border border-slate-200 p-3 hover:border-primary transition" data-testid={`kanban-card-${o.order_no}`}>
                        <button onClick={() => setSelected(o.id)} className="w-full text-left">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm text-slate-800">{o.order_no}</span>
                            {o.priority !== "normal" && <Badge className={PRIORITY_BADGE[o.priority]}>{PRIORITY_LABEL[o.priority]}</Badge>}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{o.customer_name || "Tanpa nama"}</div>
                          <div className="flex items-center justify-between mt-2 text-xs">
                            <span className={`flex items-center gap-1 ${o.is_overdue ? "text-rose-600 font-medium" : "text-slate-400"}`}>
                              <Clock size={12} /> {o.due_date ? relDay(o.due_date) : "-"}
                            </span>
                            <span className="flex items-center gap-1 text-slate-400">
                              {o.fulfillment === "delivery" ? <Truck size={12} /> : <PackageIcon size={12} />}
                            </span>
                          </div>
                          <div className="text-xs font-semibold text-slate-700 mt-1">{rupiah(o.total)}</div>
                          {(o.florist_names || []).length > 0 && (
                            <div className="text-[11px] text-primary mt-1 truncate">👤 {o.florist_names.join(", ")}</div>
                          )}
                        </button>
                        {canUsage && (
                          <button onClick={() => setUsageOrder(o)} data-testid={`record-usage-${o.order_no}`}
                            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary hover:text-white transition">
                            <Sparkles size={13} /> Catat Bahan & Selesaikan
                          </button>
                        )}
                      </div>
                    ))}
                    {!items.length && <div className="text-center text-xs text-slate-300 py-8">Kosong</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <DisplayTab branch={branch} />
      )}

      {selected && <OrderDetailModal orderId={selected} transitions={transitions} onClose={() => setSelected(null)} onChanged={load} />}
      {usageOrder && <MaterialUsageModal type="order" order={usageOrder} onClose={() => setUsageOrder(null)} onSaved={load} />}
    </>
  );
}

function DisplayTab({ branch }) {
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [addStock, setAddStock] = useState(null);
  const [editUsage, setEditUsage] = useState(null);
  const { isOwner } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, usages] = await Promise.all([
        api.get("/products", { active_only: true }),
        api.get("/production/usage", { type: "display", ...(branch ? { branch_id: branch.id } : {}) }).catch(() => []),
      ]);
      setProducts((prods || []).filter((p) => p.is_display));
      setHistory(usages || []);
    } finally { setLoading(false); }
  }, [branch]);
  useEffect(() => { load(); }, [load]);

  const delUsage = async (u) => {
    if (!window.confirm(`Hapus pencatatan ${u.usage_no}? Stok bahan dikembalikan & stok produk display dikurangi.`)) return;
    try { await api.del(`/production/usage/${u.id}`); load(); } catch (e) { alert(e.message); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setNewOpen(true)} data-testid="new-display-btn"><Plus size={16} /> Buat Produk Display Toko Baru</Button>
      </div>
      {loading ? <Loading /> : (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Produk Display Toko ({products.length})</h3>
            {!products.length ? <EmptyState icon={Store} title="Belum ada produk display" message="Buat produk display pertama Anda." /> : (
              <div className="space-y-2">
                {products.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-2 rounded-xl border border-slate-100" data-testid={`display-product-${p.id}`}>
                    <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                      {p.photo_file_id ? <img src={fileUrl(p.photo_file_id)} alt="" className="w-full h-full object-cover" /> : <span>💐</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
                      <div className="text-xs text-slate-400">Jual {rupiah(p.price)} · Modal {rupiah(p.cost_price || 0)}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setAddStock(p)} data-testid={`add-stock-${p.id}`}><Plus size={14} /> Stok</Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Riwayat Produksi Display</h3>
            {!history.length ? <EmptyState icon={Sparkles} title="Belum ada riwayat" message="Produksi display akan muncul di sini." /> : (
              <div className="space-y-2">
                {history.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-2 p-2 rounded-xl border border-slate-100" data-testid={`usage-history-${u.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{u.product_name} <span className="text-slate-400">×{u.produced_qty}</span></div>
                      <div className="text-xs text-slate-400">{u.usage_no} · Modal {rupiah(u.total_cost)} · {u.created_by_name}</div>
                    </div>
                    {isOwner && <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setEditUsage(u)} className="text-primary text-xs" data-testid={`edit-usage-${u.id}`}>Edit</button>
                      <button onClick={() => delUsage(u)} className="text-rose-500 text-xs" data-testid={`del-usage-${u.id}`}>Hapus</button>
                    </div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
      {newOpen && <MaterialUsageModal type="display" onClose={() => setNewOpen(false)} onSaved={load} />}
      {addStock && <MaterialUsageModal type="display" product={addStock} onClose={() => setAddStock(null)} onSaved={load} />}
      {editUsage && <MaterialUsageModal editUsage={editUsage} onClose={() => setEditUsage(null)} onSaved={load} />}
    </>
  );
}
